import snakecaseKeys from 'snakecase-keys';
import { restClient } from '../../client';
import * as batshit from '@yornaath/batshit';


export type ClaimsMappingType = 'candid' | 'sohar';


export type CandidPayor = {
  id: string;
  payerId: string;
  payerName: string;
  payerUuid?: string;
};


export type ClaimsMapping = {
  id: string;
  candidPayorId: string;
  /** null means the mapping covers all states */
  stateId: string | null;
  insuranceCompanyId: string;
};


export const getAllClaimsPayors = () => {
  return restClient.request<{
    data: CandidPayor[];
  }>('/api/v1/claims/payor');
};


export type CreateClaimsPayorParams = Omit<CandidPayor, 'id'>;
export const createClaimsPayor = (params: CreateClaimsPayorParams) => {
  return restClient.request<{ data: string }>('/api/v1/claims/payor', {
    method: 'POST',
    data: snakecaseKeys(params)
  });
};


export type UpdateClaimsPayorParams = Omit<CandidPayor, 'id'>;
export const updateClaimsPayor = (candidPayorId: string, params: UpdateClaimsPayorParams) => {
  return restClient.request<{ data: string }>(`/api/v1/claims/payor/${candidPayorId}`, {
    method: 'PUT',
    data: snakecaseKeys(params)
  });
};


export const getClaimMappingConflicts = (mappingType: ClaimsMappingType) => {
  return restClient.request<{
    data: ClaimsMapping[][];
  }>(`/api/v1/claims/mapping/${mappingType}/conflicts`);
};


export const getClaimMappingsForPayor = (mappingType: ClaimsMappingType, candidPayorId: string) => {
  return restClient.request<{
    data: ClaimsMapping[];
  }>(`/api/v1/claims/mapping/${mappingType}/for-payor/${candidPayorId}`);
};


export const getClaimMappingForInsuranceCompanies = (mappingType: ClaimsMappingType, insuranceCompanyIds: string[]) => {
  return restClient.request<{
    data: {
      insuranceCompanyId: string;
      payers: ClaimsMapping[];
    }[];
  }>(`/api/v1/claims/mapping/${mappingType}/for-insurance-companies`, {
    method: 'POST',
    data: {
      insurance_company_ids: insuranceCompanyIds
    }
  });
};

export const getClaimMappingForInsuranceCompaniesBatch = batshit.create({
  fetcher: async (params: { mappingType: ClaimsMappingType; insuranceCompanyId: string }[]) => {
    const soharQueries = params.filter(p => p.mappingType === 'sohar');
    const soharResponse = await getClaimMappingForInsuranceCompanies('sohar', soharQueries.map(c => c.insuranceCompanyId));

    const candidQueries = params.filter(p => p.mappingType === 'candid');
    const candidResponse = await getClaimMappingForInsuranceCompanies('candid', candidQueries.map(c => c.insuranceCompanyId));

    return {
      candid: candidResponse.data,
      sohar: soharResponse.data,
    };
  },
  resolver: (response, key) => response[key.mappingType].find((item) => item.insuranceCompanyId === key.insuranceCompanyId) ?? null,
  scheduler: batshit.windowScheduler(10)
});


export const getClaimMappingForInsuranceCompany = async (mappingType: ClaimsMappingType, insuranceCompanyId: string) => {
  const result = await getClaimMappingForInsuranceCompaniesBatch.fetch({ mappingType, insuranceCompanyId });

  return result?.payers;
};


export type CreateClaimMappingParams = {
  candidPayorId: string;
  insuranceCompanyId: string;
  stateIds: (string | null)[];
};
export const createClaimMapping = (mappingType: ClaimsMappingType, params: CreateClaimMappingParams) => {
  return restClient.request(`/api/v1/claims/mapping/${mappingType}/for-payor/${params.candidPayorId}`, {
    method: 'POST',
    data: {
      insurance_company_id: params.insuranceCompanyId,
      state_ids: params.stateIds,
    }
  });
};


export const removeClaimMapping = (mappingType: ClaimsMappingType, params: {
  candidPayorId: string;
  candidMappingId: string;
  force?: boolean;
}) => {
  return restClient.request(`/api/v1/claims/mapping/${mappingType}/for-payor/${params.candidPayorId}/${params.candidMappingId}`, {
    method: 'DELETE',
    data: {
      force: params.force,
    }
  });
};
